/**
 * @file overview-view.ts
 * @description Как показать сводку клиента на «Обзоре» (phase19.31, SC68):
 * строки «Требует внимания», срок задачи, «Как покупает», согласие на
 * рекламу. Чистые функции и реестры — блоки только рисуют.
 *
 * Новый вид «внимания» = запись в `ATTENTION_META` (тон, подпись под
 * текстом, куда ведёт строка), а не ветка в компоненте.
 */

import type { AttentionItem, AttentionTone } from "../../ui/AttentionList";
import type {
  CustomerAttentionDto,
  CustomerAttentionKind,
  CustomerOverviewBuyingDto,
  CustomerOverviewCountDto,
  CustomerOverviewDto,
  CustomerOverviewTaskDto,
} from "../../../lib/api-customers-overview";
import { pluralRu } from "../../../lib/plural";
import { formatShopDate, formatShopDateTime } from "../../../lib/shop-time";

/** Куда ведут строки сводки (адреса строит страница: ей известен магазин). */
export interface OverviewLinks {
  order: (orderId: string) => string;
  /** Вкладка «Задачи» карточки клиента. */
  tasks: string;
  /** Переписка с клиентом (до окна чата SC64 — страница «Сообщения»). */
  chat: (conversationId?: string) => string;
}

interface AttentionMeta {
  tone: AttentionTone;
  caption: (since: string) => string;
  to: (item: CustomerAttentionDto, links: OverviewLinks) => string;
}

/** Реестр видов «Требует внимания»: тон, подпись, переход. */
const ATTENTION_META: Record<CustomerAttentionKind, AttentionMeta> = {
  UNPAID_ORDER: {
    tone: "warning",
    caption: (since) => `ждёт оплаты с ${formatShopDate(since)}`,
    to: (item, links) => (item.orderId ? links.order(item.orderId) : links.tasks),
  },
  RETURN_IN_PROGRESS: {
    tone: "warning",
    caption: (since) => `открыт ${formatShopDate(since)}`,
    to: (item, links) => (item.orderId ? links.order(item.orderId) : links.tasks),
  },
  OVERDUE_TASK: {
    tone: "danger",
    caption: (since) => `срок ${formatShopDate(since)}`,
    to: (_item, links) => links.tasks,
  },
  UNREAD_MESSAGE: {
    tone: "info",
    caption: (since) => formatShopDateTime(since),
    to: (item, links) => links.chat(item.conversationId),
  },
};

export function attentionItems(items: CustomerAttentionDto[], links: OverviewLinks): AttentionItem[] {
  return items.map((item, index) => {
    const meta = ATTENTION_META[item.kind];
    return {
      key: `${item.kind}:${item.orderId ?? item.activityId ?? item.conversationId ?? index}`,
      tone: meta.tone,
      title: item.title,
      caption: meta.caption(item.since),
      to: meta.to(item, links),
    };
  });
}

/** Срок задачи словами: «просрочено · 20.09.2026», «до 20.09.2026», «без срока». */
export function taskDueText(task: Pick<CustomerOverviewTaskDto, "dueAt" | "overdue">): string {
  if (!task.dueAt) return "без срока";
  return task.overdue ? `просрочено · ${formatShopDate(task.dueAt)}` : `до ${formatShopDate(task.dueAt)}`;
}

/** «СДЭК ПВЗ (3) · Самовывоз (1)»; одна строка — без числа. */
export function countsText(rows: CustomerOverviewCountDto[]): string | null {
  if (rows.length === 0) return null;
  if (rows.length === 1) return rows[0]!.label;
  return rows.map((r) => `${r.label} (${r.count})`).join(" · ");
}

/** «Сила жизни (3 шт), Иван-чай (2 шт)» — три самых частых. */
export function topProductsText(buying: CustomerOverviewBuyingDto): string | null {
  if (buying.topProducts.length === 0) return null;
  return buying.topProducts
    .slice(0, 3)
    .map((p) => `${p.name} (${p.quantity} шт)`)
    .join(", ");
}

const DAY_FORMS: [string, string, string] = ["день", "дня", "дней"];

/** «Покупает примерно раз в 20 дней» — только когда сервер посчитал промежуток. */
export function frequencyText(buying: CustomerOverviewBuyingDto): string | null {
  const n = buying.avgDaysBetweenOrders;
  if (n === null || n <= 0) return null;
  const days = Math.round(n);
  return `Покупает примерно раз в ${days} ${pluralRu(days, DAY_FORMS)}`;
}

/** Без привычного ритма «давно» — от трёх месяцев. */
const LONG_PAUSE_DAYS = 90;

/**
 * «Не покупал 95 дней» — только если давно: вдвое дольше обычного
 * промежутка между заказами, а когда промежутка нет — от 90 дней.
 */
export function pauseText(buying: CustomerOverviewBuyingDto): string | null {
  const since = buying.daysSinceLastOrder;
  if (since === null) return null;
  const avg = buying.avgDaysBetweenOrders;
  const threshold = avg && avg > 0 ? Math.max(avg * 2, 30) : LONG_PAUSE_DAYS;
  if (since < threshold) return null;
  return `Не покупал ${since} ${pluralRu(since, DAY_FORMS)}`;
}

/** Строка согласия на рекламу. */
export function marketingText(consent: CustomerOverviewDto["marketingConsent"]): string {
  if (!consent.granted) return "Реклама: согласия нет";
  return consent.at ? `Реклама: согласие с ${formatShopDate(consent.at)}` : "Реклама: согласие есть";
}
